// The printable sticker sheets: a standalone HTML document (own @page rules, sizes in mm) that the print
// dialog shows in an iframe and prints from there, so nothing of the app's layout can shift a label.
import { qrSvg } from "./qr";
import { stickerUrl } from "@/config/stickers";

// All sizes in millimetres. A4 = 210 × 297.
// The QR box holds the code and its 4-module white quiet zone (qr.js), so the text starts after it. A module is a
// whole number of printer dots (labels 1/30", plain 1/20": 10 / 15 dots at 300 dpi) and every box starts on the
// 1/150" grid of the page, so all modules print the same size.
const INCH = 25.4;
const GRID = INCH / 150;
export const LAYOUTS = {
  // label sheets 70 × 37 mm, 3 × 8 = 24 per A4, no side margins, 0.5 mm top/bottom
  labels: {
    id: "labels",
    title: "Coli de etichete A4, 70 × 37 mm",
    subtitle: "3 × 8 = 24 pe coală; puteți începe de la orice poziție",
    cols: 3,
    rows: 8,
    width: 70,
    height: 37,
    left: 0,
    top: 0.5,
    // QR box: from the left edge, module size, at most this big (a longer code gets smaller modules)
    inset: 1.2,
    module: INCH / 30,
    qrMax: 35.5,
    gap: 0.4,
    padding: 3, // right
    font: { brand: 2.5, what: 2.7, serial: 2.4, room: 2.2, tag: 4.7, lines: 2 },
    cutLines: false,
  },
  // plain A4: bigger QR, dashed lines to cut along
  plain: {
    id: "plain",
    title: "Hârtie A4 simplă, cod QR mare",
    subtitle: "2 × 5 = 10 pe pagină, cod QR de 42 mm, linii de tăiere",
    cols: 2,
    rows: 5,
    width: 95,
    height: 55,
    left: 10,
    top: 11,
    inset: 1.5,
    module: INCH / 20,
    qrMax: 53,
    gap: 0.5,
    padding: 4,
    font: { brand: 3.4, what: 3.4, serial: 3, room: 2.8, tag: 6.2, lines: 3 },
    cutLines: true,
  },
};

const snap = (value) => Math.round(value / GRID) * GRID;
const mm = (value) => `${Number(value.toFixed(3))}mm`;

// the QR box (quiet zone included) for codes `modules` wide, and where the text starts
function qrBox(L, modules) {
  const box = modules * Math.min(L.module, L.qrMax / modules);
  // + GRID: snapping moves a box by up to half of it
  const textLeft = L.inset + box + L.gap + GRID;
  return { box, textLeft, textWidth: L.width - textLeft - L.padding };
}

export function perPage(layout) {
  return layout.cols * layout.rows;
}

// Rows of Echipamente / Inventariere / the import → what a sticker shows
export function stickerItem(row) {
  return {
    tag: String(row.tag || "").toUpperCase(),
    type: row.equipmentType || row.type || "",
    model: row.model || "",
    serial: row.serial_number || row.serial || "",
    registration: row.registration_number || row.registration || "",
    location: row.location || "",
  };
}

const escapeHtml = (value) =>
  String(value == null ? "" : value).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// tag → promise of { svg, modules }
const svgCache = new Map();
function qrFor(tag) {
  if (!svgCache.has(tag)) {
    const code = qrSvg(stickerUrl(tag));
    svgCache.set(tag, code);
    code.catch(() => svgCache.delete(tag));
  }
  return svgCache.get(tag);
}
// one code after the other, letting the page draw in between (each new code takes some ms: qr.js reads it back)
async function qrCodes(tags) {
  const codes = [];
  for (const tag of tags) {
    const fresh = !svgCache.has(tag);
    codes.push(await qrFor(tag));
    if (fresh) await new Promise((resolve) => setTimeout(resolve, 0));
  }
  return codes;
}

function styles(layout, { box, textLeft, textWidth }) {
  const L = layout;
  return `
@page { size: 210mm 297mm; margin: 0; }
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; background: #fff; }
body { font-family: Arial, Helvetica, "Liberation Sans", sans-serif; color: #000; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.sheet { position: relative; width: 210mm; height: 297mm; overflow: hidden; background: #fff; break-after: page; page-break-after: always; }
.sheet:last-child { break-after: auto; page-break-after: auto; }
.st { position: absolute; width: ${L.width}mm; height: ${L.height}mm; padding: 0 ${L.padding}mm 0 ${mm(textLeft)}; display: flex; align-items: center; overflow: hidden; }
.st__qr { position: absolute; width: ${mm(box)}; height: ${mm(box)}; }
.st__qr svg { display: block; width: 100%; height: 100%; }
.st__text { flex: none; width: ${mm(textWidth)}; min-width: 0; display: flex; flex-direction: column; justify-content: center; }
.st__brand { font-size: ${L.font.brand}mm; font-weight: 700; letter-spacing: 0.03em; text-transform: uppercase; margin-bottom: 0.8mm; }
.st__what { font-size: ${L.font.what}mm; line-height: 1.18; overflow: hidden; display: -webkit-box; -webkit-line-clamp: ${L.font.lines}; -webkit-box-orient: vertical; overflow-wrap: anywhere; }
.st__serial { font-size: ${L.font.serial}mm; margin-top: 0.6mm; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.st__what + .st__serial { margin-top: 0.8mm; }
/* long rooms („Biblioteca, Parter, Sala de împrumut nr. 1 Documente ştiinţifice / beletristică”, 79 characters)
   wrap instead of being cut */
.st__room { font-size: ${L.font.room}mm; line-height: 1.15; margin-top: 0.6mm; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow-wrap: anywhere; }
.st__tag { font-family: Consolas, "DejaVu Sans Mono", "Liberation Mono", "Courier New", monospace; font-size: ${L.font.tag}mm; font-weight: 700; letter-spacing: 0.04em; margin-top: 1mm; white-space: nowrap; }
.cut { position: absolute; border: 0 dashed #8a8a8a; }
.cut--v { width: 0; border-left-width: 0.25mm; }
.cut--h { height: 0; border-top-width: 0.25mm; }
.slot { display: none; }
@media screen {
  body { background: #dfe6e2; padding: 12px 0; }
  .sheet { margin: 0 auto 14px; background: #fff; box-shadow: 0 2px 12px rgba(0, 0, 0, 0.22); }
  .slot { display: flex; position: absolute; border: 1px dashed #c3d0c9; align-items: center; justify-content: center; font: 600 3mm Arial, sans-serif; color: #9aa9a1; }
  .slot--used { background: repeating-linear-gradient(135deg, #f1f4f2 0 2mm, #e6ece8 2mm 4mm); }
}`;
}

/**
 * The whole document for `items` (rows with tag, type, model, serial, nr. inventar, room). `start` = first free position on the
 * first sheet (1-based, label sheets only). Returns { html, pages, count }.
 */
export async function buildStickerDocument(items, { layout: layoutId = "labels", start = 1 } = {}) {
  const L = LAYOUTS[layoutId] || LAYOUTS.labels;
  const stickers = (items || []).map(stickerItem).filter((s) => s.tag);
  const n = perPage(L);
  const skip = L.cutLines ? 0 : Math.min(Math.max(Number(start) || 1, 1), n) - 1;
  const total = skip + stickers.length;
  const pages = Math.max(1, Math.ceil(total / n));
  const codes = await qrCodes(stickers.map((s) => s.tag));
  const geometry = qrBox(L, Math.max(0, ...codes.map((c) => c.modules)));

  const pos = (slot) => {
    const col = slot % L.cols;
    const row = Math.floor(slot / L.cols);
    return { x: L.left + col * L.width, y: L.top + row * L.height };
  };
  let body = "";
  for (let p = 0; p < pages; p++) {
    let sheet = "";
    let usedRows = 0;
    for (let slot = 0; slot < n; slot++) {
      const index = p * n + slot; // position over the whole run, skipped ones included
      const { x, y } = pos(slot);
      const at = `left:${x}mm;top:${y}mm;width:${L.width}mm;height:${L.height}mm`;
      const sticker = index >= skip ? stickers[index - skip] : null;
      if (index < skip) {
        sheet += `<div class="slot slot--used" style="${at}">folosit</div>`;
        continue;
      }
      if (!sticker) {
        sheet += `<div class="slot" style="${at}"></div>`;
        continue;
      }
      usedRows = Math.floor(slot / L.cols) + 1;
      const what = [sticker.type, sticker.model].filter(Boolean).join(" ");
      // the QR box on the page grid, vertically centred
      const qx = snap(x + L.inset) - x;
      const qy = snap(y + (L.height - geometry.box) / 2) - y;
      sheet += `<div class="slot" style="${at}"></div>`
        + `<div class="st" style="left:${x}mm;top:${y}mm" data-tag="${escapeHtml(sticker.tag)}">`
        + `<div class="st__qr" style="left:${mm(qx)};top:${mm(qy)}">${codes[index - skip].svg}</div>`
        + `<div class="st__text">`
        + `<div class="st__brand">Suport DTI</div>`
        + (what ? `<div class="st__what">${escapeHtml(what)}</div>` : "")
        + (sticker.registration ? `<div class="st__serial">Nr. inv.: ${escapeHtml(sticker.registration)}</div>` : "")
        + (sticker.serial ? `<div class="st__serial">Seria: ${escapeHtml(sticker.serial)}</div>` : "")
        + (sticker.location ? `<div class="st__room">${escapeHtml(sticker.location)}</div>` : "")
        + `<div class="st__tag">${escapeHtml(sticker.tag)}</div>`
        + `</div></div>`;
    }
    if (L.cutLines && usedRows) {
      const height = usedRows * L.height;
      for (let c = 0; c <= L.cols; c++) {
        sheet += `<div class="cut cut--v" style="left:${L.left + c * L.width}mm;top:${L.top}mm;height:${height}mm"></div>`;
      }
      for (let r = 0; r <= usedRows; r++) {
        sheet += `<div class="cut cut--h" style="left:${L.left}mm;top:${L.top + r * L.height}mm;width:${L.cols * L.width}mm"></div>`;
      }
    }
    body += `<section class="sheet" data-page="${p + 1}">${sheet}</section>`;
  }
  const html = `<!doctype html><html lang="ro"><head><meta charset="utf-8"><title>Etichete Suport DTI</title>`
    + `<style>${styles(L, geometry)}</style></head><body class="layout-${L.id}">${body}</body></html>`;
  return { html, pages, count: stickers.length };
}
